import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import Modal from '../../containers/Modal';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import { CoreLink, DailyRegistrationForm } from '../../components';
import useDailyRegistrations from '../../hooks/useDailyRegistrations';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import { deleteRegistration, updateRegistration } from '../../store/actions/Registrations';
import useToast from '../../hooks/useToast';

import './style.scss';

const PageDailyRegistrationUpdate: React.FC = () => {
  const { buildClass, joinClasses } = useClasses('PageDailyRegistrationUpdate');
  const history = useHistory();
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const routes = useRoutes();
  const params = useParams<{
    date: string
  }>();
  const toast = useToast();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uuid = searchParams.get('uuid');
  const { date } = params;
  const { registrations } = useDailyRegistrations();
  const selectedRegistration = registrations[date]?.items[uuid];

  const [isLoading, setLoading] = React.useState(false);

  if (registrations && !selectedRegistration && date) {
    history.push(`${routes.dailyRegistration}?date=${date}`);
  }

  if (registrations && !selectedRegistration && !date) {
    history.push(routes.recoveryPlan);
  }

  return (
    <Modal
      modalVisible
      className={buildClass()}
      title={t('pages.daily_registration.title')}
      closeModal={() => {
        history.push(routes.recoveryPlan);
      }}
    >
      <div className="col-lg-8 offset-lg-2">
        <div className={buildClass('backlink-container')}>
          <CoreLink
            className={joinClasses(buildClass('backlink'), 'icon-before icon-chevron icon-orientation-left')}
            to={`${routes.dailyRegistration}?date=${date}`}
          >
            Terug
          </CoreLink>
          { selectedRegistration && (
            <DailyRegistrationForm
              isLoading={isLoading}
              initialValues={{
                date,
                frequency: String(selectedRegistration.amount),
                where: selectedRegistration.where,
                withWho: selectedRegistration.with_who,
                note: selectedRegistration.note,
                cost: selectedRegistration.cost,
              }}
              onDelete={async () => {
                setLoading(true);

                await dispatch(deleteRegistration(date, uuid)).then(() => {
                  setLoading(false);

                  // Show success toast.
                  toast.show(t('pages.daily_registration.delete.success_message'), 'success');

                  history.push(`${routes.dailyRegistration}?date=${date}`);
                }).catch((error) => {
                  if (error) {
                    setLoading(false);
                    toast.show(t('pages.daily_registration.delete.error_message'), 'error');
                  }
                });
              }}
              onSubmit={async (values) => {
                const mappedData = {
                  registration_date: values.date,
                  amount: String(values.frequency),
                  cost: values.cost,
                  where: values.where,
                  with_who: values.withWho,
                  note: values.note,
                };

                setLoading(true);

                await dispatch(updateRegistration(uuid, mappedData))
                  .then(() => {
                    setLoading(false);
                    toast.show(t('pages.daily_registration.update.success_message'), 'success');
                    history.push(`${routes.dailyRegistration}?date=${date}`);
                  })
                  .catch((err) => {
                    toast.show(t('pages.daily_registration.update.error_message'), 'error');
                  });

                history.push(`${routes.dailyRegistration}?date=${date}`);
              }}
              isEditing
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PageDailyRegistrationUpdate;
